import { translationKey } from '../../i18n';
import { localize } from '../../i18n';
import SingleLineText from '../../components/ui/SingleLineText';
import Button from '../../components/ui/FitButton';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import medicationEditChipSx from './medicationEditChipSx';
import useAppLanguage from '../../i18n';

export const MAX_DAILY_REMINDER_TIMES = 10;
export const MEDICATION_TIME_COUNT_PRESETS = [
    { count: 1, times: ['08:00'] },
    { count: 2, times: ['08:00', '18:00'] },
    { count: 3, times: ['08:00', '12:00', '18:00'] },
    { count: 4, times: ['08:00', '12:00', '18:00', '22:00'] },
];

export function normalizeMedicationTimes(times = []) {
    return Array.from(new Set((times || []).filter(Boolean))).sort();
}

export function sameMedicationTimes(a = [], b = []) {
    const left = normalizeMedicationTimes(a);
    const right = normalizeMedicationTimes(b);
    return left.length === right.length && left.every((time, index) => time === right[index]);
}

export function hasCustomMedicationTimes(times = []) {
    const normalized = normalizeMedicationTimes(times);
    if (normalized.length === 0) return false;
    return !MEDICATION_TIME_COUNT_PRESETS.some(preset => sameMedicationTimes(normalized, preset.times));
}

function openTimePicker(input) {
    if (!input) return;
    try {
        if (typeof input.showPicker === 'function') {
            input.showPicker();
            return;
        }
    } catch (_) {}
    try { input.focus(); } catch (_) {}
    try { input.click(); } catch (_) {}
}

function formatTimeKorean(time) {
    const [hour, minute] = String(time || '').split(':');
    const h = Number(hour);
    const period = localize(h < 12 ? '오전' : '오후');
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${period} ${h12}:${minute}`;
}

function getExistingMedicationTimes(med = {}) {
    return normalizeMedicationTimes((med.times || med.schedule?.times || [])
        .map(time => String(time || '').slice(0, 5))
        .filter(time => /^\d{2}:\d{2}$/.test(time)))
        .slice(0, MAX_DAILY_REMINDER_TIMES);
}

export function buildMedicationTimeOptions(existingMedications = []) {
    const groups = new Map();
    existingMedications.forEach((med) => {
        if (!med || med.frequencyType === 'AS_NEEDED' || med.schedule?.frequencyType === 'AS_NEEDED') return;
        const medTimes = getExistingMedicationTimes(med);
        if (medTimes.length === 0) return;
        const key = medTimes.join('|');
        const group = groups.get(key) || { key, times: medTimes, names: [] };
        group.names.push(med.name || localize('기존 약'));
        groups.set(key, group);
    });

    return Array.from(groups.values())
        .sort((a, b) => b.names.length - a.names.length || a.times[0].localeCompare(b.times[0]))
        .map((group) => ({
            ...group,
            source: group.names.length > 1 ? localize("{v0} 외 {v1}개", { v0: group.names[0], v1: group.names.length - 1 }) : group.names[0],
        }));
}

export function MedicationTimeOptionList({ options = [], selectedOptionKey = null, onSelect }) {
    const { language, t } = useAppLanguage();
    if (!options.length) return null;

    return (
        <Box sx={{ mt: 0.25, mb: 1.15 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.7 }}>
                <SingleLineText sx={{ fontSize: '0.82rem', fontWeight: 850, color: '#8a8a8a' }}>
                    {t('다른 약 시간으로 맞추기')}
                </SingleLineText>
                <Box
                    component="span"
                    sx={{
                        px: 0.55,
                        py: 0.15,
                        borderRadius: '999px',
                        bgcolor: '#f3f4f6',
                        color: '#9ca3af',
                        fontSize: '0.68rem',
                        fontWeight: 850,
                        lineHeight: 1.25,
                    }}
                >
                    {t('선택')}
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.8,
                    overflowX: 'auto',
                    pb: 0.45,
                    mx: -0.2,
                    px: 0.2,
                    WebkitOverflowScrolling: 'touch',
                    scrollSnapType: 'x proximity',
                }}
            >
                {options.map((option) => {
                    const selected = selectedOptionKey === option.key;
                    return (
                        <Box
                            key={option.key}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelect?.(option)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onSelect?.(option);
                                }
                            }}
                            sx={{
                                flex: '0 0 auto',
                                width: 128,
                                px: 0.9,
                                py: 0.78,
                                borderRadius: 2,
                                bgcolor: selected ? '#fffbeb' : '#fff',
                                border: selected ? '1.5px solid #ffe566' : '1px solid #e5e7eb',
                                cursor: 'pointer',
                                scrollSnapAlign: 'start',
                                '&:active': { bgcolor: selected ? '#fff3c4' : '#f8fafc' },
                            }}
                        >
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#374151', lineHeight: 1.22,
                                ...(language === 'ko' ? {} : { overflowWrap: 'anywhere' }),
                            }} noWrap={language === 'ko'}>
                                {option.names?.length > 1
                                    ? t('{name} 외 {count}개', { name: option.names[0], count: option.names.length - 1 })
                                    : option.names?.[0] || option.source}
                            </Typography>
                            <Typography sx={{ mt: 0.25, fontSize: '0.72rem', fontWeight: 750, color: selected ? '#7a6200' : '#8a7000', lineHeight: 1.25 }} noWrap>
                                {option.times.map(time => language === 'ko' ? formatTimeKorean(time) : time).join(' · ')}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export default function MedicationTimesEditor({
    value,
    onChange,
    onWarning,
    onPresetReplaceRequest,
    existingMedications = [],
}) {
    const { language, t } = useAppLanguage();
    const times = value && value.length > 0 ? value : ['08:00'];
    const [pendingFocusTimeIndex, setPendingFocusTimeIndex] = useState(null);
    const [selectedExistingTimeKey, setSelectedExistingTimeKey] = useState(null);
    const timeInputRefs = useRef([]);
    const existingTimeOptions = useMemo(
        () => buildMedicationTimeOptions(existingMedications),
        [existingMedications],
    );

    useEffect(() => {
        if (!selectedExistingTimeKey) return;
        if (!existingTimeOptions.some(option => option.key === selectedExistingTimeKey)) {
            setSelectedExistingTimeKey(null);
        }
    }, [existingTimeOptions, selectedExistingTimeKey]);

    useEffect(() => {
        if (pendingFocusTimeIndex === null) return undefined;
        const timer = setTimeout(() => {
            const input = timeInputRefs.current[pendingFocusTimeIndex];
            openTimePicker(input);
            setPendingFocusTimeIndex(null);
        }, 80);
        return () => clearTimeout(timer);
    }, [pendingFocusTimeIndex, times.length]);

    const applyPreset = (preset) => {
        setSelectedExistingTimeKey(null);
        if (sameMedicationTimes(times, preset.times)) {
            onChange(normalizeMedicationTimes(preset.times));
            return;
        }
        if (hasCustomMedicationTimes(times) && onPresetReplaceRequest) {
            onPresetReplaceRequest(preset);
            return;
        }
        onChange(normalizeMedicationTimes(preset.times));
    };

    const applyExistingTimeOption = (option) => {
        const preset = { ...option, times: normalizeMedicationTimes(option.times), source: 'existing-medication' };
        if (selectedExistingTimeKey === option.key) {
            setSelectedExistingTimeKey(null);
            return;
        }
        setSelectedExistingTimeKey(option.key);
        if (sameMedicationTimes(times, preset.times)) {
            onChange(preset.times);
            return;
        }
        onChange(preset.times);
    };

    const updateTime = (index, nextValue) => {
        setSelectedExistingTimeKey(null);
        if (nextValue && times.some((item, itemIndex) => itemIndex !== index && item === nextValue)) {
            onWarning?.(t('이미 같은 시간이 있어요'));
            return;
        }
        const nextTimes = [...times];
        nextTimes[index] = nextValue;
        // Keep the edited row in place until the user finishes choosing a time.
        onChange(nextTimes);
    };

    const removeTime = (index) => {
        setSelectedExistingTimeKey(null);
        if (times.length <= 1) return;
        onChange(normalizeMedicationTimes(times.filter((_, itemIndex) => itemIndex !== index)));
    };

    const addTime = () => {
        setSelectedExistingTimeKey(null);
        if (times.length >= MAX_DAILY_REMINDER_TIMES) {
            onWarning?.(t('하루 {count}번까지만 설정할 수 있어요', { count: MAX_DAILY_REMINDER_TIMES }));
            return;
        }
        if (times.some(time => !time)) {
            onWarning?.(t('비어있는 시간을 먼저 선택해주세요'));
            return;
        }
        onChange([...times, '']);
        setPendingFocusTimeIndex(times.length);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.45 }}>
            <Box>
                <SingleLineText sx={{ fontSize: '0.9rem', fontWeight: 900, color: '#1f2937', mb: 0.9 }}>
                    {t('하루에 몇 번')}
                </SingleLineText>
                <Box sx={{ display: 'flex', gap: 0.85, rowGap: 0.8, flexWrap: 'wrap' }}>
                    {MEDICATION_TIME_COUNT_PRESETS.map(preset => (
                        <Chip
                            key={preset.count}
                            label={language === 'en' && preset.count === 1 ? '1 time' : t('{count}번', { count: preset.count })}
                            onClick={() => applyPreset(preset)}
                            sx={medicationEditChipSx(sameMedicationTimes(times, preset.times))}
                        />
                    ))}
                </Box>
            </Box>
            <MedicationTimeOptionList
                options={existingTimeOptions}
                selectedOptionKey={selectedExistingTimeKey}
                onSelect={applyExistingTimeOption}
            />
            {times.map((time, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <TextField
                        type="time"
                        value={time}
                        inputRef={(node) => {
                            timeInputRefs.current[index] = node;
                        }}
                        onChange={(event) => updateTime(index, event.target.value)}
                        onClick={(event) => {
                            const input = timeInputRefs.current[index];
                            if (!input) return;
                            if (event.target === input) {
                                try {
                                    if (typeof input.showPicker === 'function') {
                                        input.showPicker();
                                        return;
                                    }
                                } catch (_) {}
                                try { input.focus(); } catch (_) {}
                                return;
                            }
                            openTimePicker(input);
                        }}
                        fullWidth
                        inputProps={{ step: 300 }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 52,
                                borderRadius: 3,
                                bgcolor: '#fff',
                                fontWeight: 800,
                                fontSize: '1rem',
                            },
                            '& .MuiInputBase-input': {
                                py: 1.35,
                            },
                        }}
                    />
                    <IconButton
                        disabled={times.length <= 1}
                        onClick={() => removeTime(index)}
                        sx={{ width: 44, height: 44, color: '#aaa', '&:disabled': { color: '#ddd' } }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            ))}
            <Button
                onClick={addTime}
                startIcon={<AddIcon />}
                sx={{ alignSelf: 'flex-start', color: '#5a4a00', fontWeight: 900, borderRadius: 2.5 }}
            >
                {t('시간 추가')}
            </Button>
        </Box>
    );
}
